import React from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";

export type CategoryListFiltersProps = {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  depthFilter: string;
  onDepthFilterChange: (value: string) => void;
  hasProductsFilter: boolean;
  onHasProductsFilterChange: (value: boolean) => void;
  uniqueDepths: number[];
  onClear: () => void;
  activeCount: number;
};

const CategoryListFilters: React.FC<CategoryListFiltersProps> = ({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  depthFilter,
  onDepthFilterChange,
  hasProductsFilter,
  onHasProductsFilterChange,
  uniqueDepths,
  onClear,
  activeCount
}) => {
  const { t } = useKivunovaTranslation();

  return (
    <Box sx={{ p: 2, minWidth: 320, maxWidth: 400 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
        {t("filters", "Filters")}
        {activeCount > 0 && (
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ ml: 1 }}
          >
            ({activeCount} {t("active-count", "active")})
          </Typography>
        )}
      </Typography>
      <Box display="flex" flexDirection="column" gap={2}>
        <TextField
          size="small"
          fullWidth
          label={t("search-categories", "Search categories")}
          placeholder={t("search-categories", "Search categories")}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <FormControl size="small" fullWidth>
          <InputLabel>{t("status", "Status")}</InputLabel>
          <Select
            value={statusFilter}
            label={t("status", "Status")}
            onChange={(e) => onStatusFilterChange(e.target.value)}
          >
            <MenuItem value="all">{t("all-status", "All status")}</MenuItem>
            <MenuItem value="active">{t("active-status", "Active")}</MenuItem>
            <MenuItem value="inactive">
              {t("inactive-status", "Inactive")}
            </MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>{t("depth-level", "Depth level")}</InputLabel>
          <Select
            value={depthFilter}
            label={t("depth-level", "Depth level")}
            onChange={(e) => onDepthFilterChange(e.target.value)}
          >
            <MenuItem value="all">{t("all-levels", "All levels")}</MenuItem>
            {uniqueDepths.map((depth) => (
              <MenuItem key={depth} value={String(depth)}>
                {t("level", "Level")} {depth}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box display="flex" alignItems="center">
          <Checkbox
            checked={hasProductsFilter}
            onChange={(e) => onHasProductsFilterChange(e.target.checked)}
            size="small"
          />
          <Typography variant="body2">
            {t("has-products-only", "Has products only")}
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<ClearIcon />}
          onClick={onClear}
          disabled={activeCount === 0}
          sx={{ alignSelf: "flex-start", textTransform: "none" }}
        >
          {t("clear-all", "Clear all")}
        </Button>
      </Box>
    </Box>
  );
};

export default CategoryListFilters;
